import { useSelector } from 'react-redux';

import { useGame } from '@/features/game/hooks/use-game';
import { gameDataLoadingSelector } from '@/features/game/store/game-data';
import { GameDataLoadingState } from '@/features/game/types';
import styles from '@/features/game-client/game-client.module.scss';
import { GenericProvider, Provider } from '@/features/game-context/types';
import { isGenericProvider } from '@/features/game-window-integration/providers/generic-provider/utils.ts';
import { Aristocrat } from '@/features/providers/aristocrat/aristocrat';
import { Awager } from '@/features/providers/awager/awager';
import { Dgc } from '@/features/providers/dgc/dgc';
import { Everi } from '@/features/providers/everi/everi';
import { Evolution } from '@/features/providers/evolution/evolution';
import { Fusion } from '@/features/providers/fusion/fusion';
import { GenericProviderClient } from '@/features/providers/generic-provider/generic-provider.tsx';
import { Igt } from '@/features/providers/igt/igt';
import { Lnw } from '@/features/providers/lnw/lnw';
import { Playtech } from '@/features/providers/playtech/playtech';
import { Pragmatic } from '@/features/providers/pragmatic/pragmatic';
import { GameDataProp } from '@/features/providers/types';

const providerComponentMap = new Map<
  Provider | GenericProvider,
  React.FC<GameDataProp>
>([
  [Provider.IGT, Igt],
  [Provider.LNW, Lnw],
  [Provider.DGC, Dgc],
  [Provider.EVOLUTION, Evolution],
  [Provider.EVERI, Everi],
  [Provider.ARISTOCRAT, Aristocrat],
  [Provider.PLAYTECH, Playtech],
  [Provider.PLAYTECH_POKER, Playtech],
  [Provider.PRAGMATIC, Pragmatic],
  [Provider.AWAGER, Awager],
  [Provider.FUSION, Fusion],
]);

export const GameClient = () => {
  const { gameData } = useGame();

  const gameDataLoading = useSelector(gameDataLoadingSelector);

  if (gameDataLoading === GameDataLoadingState.Loading) return null;

  if (!gameData || gameDataLoading === GameDataLoadingState.Failed) {
    return <p>Failed to load game</p>;
  }

  if (
    !providerComponentMap.get(gameData.provider) &&
    isGenericProvider(gameData.provider)
  ) {
    providerComponentMap.set(gameData.provider, GenericProviderClient);
  }

  const ProviderComponent = providerComponentMap.get(gameData.provider);

  if (!ProviderComponent) {
    return <div>Provider not found</div>;
  }

  return (
    <div className={styles.gameClient}>
      <ProviderComponent data={gameData} />
    </div>
  );
};
